import { useState, useEffect, useCallback } from 'react';
import { Settings, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, getApiErrorMessage } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useTheme } from '@/hooks/useTheme';
import EquipmentGroupsSection from './EquipmentGroupsSection';

interface TechnicalSpec {
  inverterType?: string;
  inverterCount?: number;
  panelType?: string;
  panelCount?: number;
  batteryCapacityMwh?: number | null;
  gridConnectionVoltage?: string;
  meteringPointCode?: string;
  scadaAvailable?: boolean;
  scadaVendor?: string;
  communicationProtocol?: string;
  dataRefreshInterval?: string;
  tiltAngleDeg?: number | null;
  azimuthDeg?: number | null;
  installedCapacityDcMw?: number | null;
  installedCapacityAcMw?: number | null;
  panelNominalPowerW?: number | null;
  inverterTotalPowerKw?: number | null;
  dcAcRatio?: number | null;
}

interface Props {
  assetId: string;
  initialData?: TechnicalSpec;
  onSaved: () => void;
}

interface ValidationHint {
  field: string;
  message: string;
}

function toNum(v: string): number | null {
  const trimmed = v.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (Number.isNaN(n)) return null;
  return n;
}

function toNumberOrNull(value: string | null | undefined): number | null {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function fmt(n: number | null | undefined, digits = 4): string {
  if (n == null || Number.isNaN(n)) return '';
  return n.toFixed(digits).replace(/\.?0+$/, '');
}

function withinPct(a: number, b: number, pct = 3): boolean {
  if (b === 0) return a === 0;
  return Math.abs((a - b) / b) * 100 <= pct;
}

export default function TabTechnical({ assetId, initialData, onSaved }: Props) {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { isDark } = useTheme();
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const [form, setForm] = useState<Record<string, string>>({
    inverterType: '',
    inverterCount: '',
    panelType: '',
    panelCount: '',
    batteryCapacityMwh: '',
    gridConnectionVoltage: '',
    meteringPointCode: '',
    scadaAvailable: 'false',
    scadaVendor: '',
    communicationProtocol: '',
    dataRefreshInterval: '',
    tiltAngleDeg: '',
    azimuthDeg: '',
    installedCapacityDcMw: '',
    installedCapacityAcMw: '',
    panelNominalPowerW: '',
    inverterTotalPowerKw: '',
    dcAcRatio: '',
  });

  const [manualDcAc, setManualDcAc] = useState(false);
  const [hints, setHints] = useState<ValidationHint[]>([]);

  const populateForm = useCallback((spec: TechnicalSpec | undefined) => {
    if (!spec) return;
    setForm({
      inverterType: spec.inverterType ?? '',
      inverterCount: spec.inverterCount != null ? String(spec.inverterCount) : '',
      panelType: spec.panelType ?? '',
      panelCount: spec.panelCount != null ? String(spec.panelCount) : '',
      batteryCapacityMwh: spec.batteryCapacityMwh != null ? String(spec.batteryCapacityMwh) : '',
      gridConnectionVoltage: spec.gridConnectionVoltage ?? '',
      meteringPointCode: spec.meteringPointCode ?? '',
      scadaAvailable: spec.scadaAvailable ? 'true' : 'false',
      scadaVendor: spec.scadaVendor ?? '',
      communicationProtocol: spec.communicationProtocol ?? '',
      dataRefreshInterval: spec.dataRefreshInterval ?? '',
      tiltAngleDeg: spec.tiltAngleDeg != null ? String(spec.tiltAngleDeg) : '',
      azimuthDeg: spec.azimuthDeg != null ? String(spec.azimuthDeg) : '',
      installedCapacityDcMw: spec.installedCapacityDcMw != null ? String(spec.installedCapacityDcMw) : '',
      installedCapacityAcMw: spec.installedCapacityAcMw != null ? String(spec.installedCapacityAcMw) : '',
      panelNominalPowerW: spec.panelNominalPowerW != null ? String(spec.panelNominalPowerW) : '',
      inverterTotalPowerKw: spec.inverterTotalPowerKw != null ? String(spec.inverterTotalPowerKw) : '',
      dcAcRatio: spec.dcAcRatio != null ? String(spec.dcAcRatio) : '',
    });
    if (spec.dcAcRatio != null) {
      setManualDcAc(true);
    }
  }, []);

  useEffect(() => {
    populateForm(initialData);
  }, [initialData, populateForm]);

  // Auto-calculate dcAcRatio when DC or AC changes (only if user hasn't manually set it)
  useEffect(() => {
    if (manualDcAc) return;
    const dc = toNum(form.installedCapacityDcMw);
    const ac = toNum(form.installedCapacityAcMw);
    if (dc != null && ac != null && ac > 0) {
      const ratio = dc / ac;
      setForm((prev) => ({ ...prev, dcAcRatio: String(ratio) }));
    }
  }, [form.installedCapacityDcMw, form.installedCapacityAcMw, manualDcAc]);

  // Validation hints
  useEffect(() => {
    const nextHints: ValidationHint[] = [];

    const invKw = toNum(form.inverterTotalPowerKw);
    const acMw = toNum(form.installedCapacityAcMw);
    if (invKw != null && acMw != null && acMw > 0) {
      const invMw = invKw / 1000;
      if (!withinPct(invMw, acMw, 3)) {
        nextHints.push({
          field: 'inverterTotalPowerKw',
          message: `Inverter total power (${fmt(invMw, 3)} MW) differs from installed AC capacity (${fmt(acMw, 3)} MW) by > 3%`,
        });
      }
    }

    const panels = toNum(form.panelCount);
    const panelW = toNum(form.panelNominalPowerW);
    const dcMw = toNum(form.installedCapacityDcMw);
    if (panels != null && panelW != null && panels > 0 && panelW > 0 && dcMw != null && dcMw > 0) {
      const calculatedDcMw = (panels * panelW) / 1_000_000;
      if (!withinPct(calculatedDcMw, dcMw, 3)) {
        nextHints.push({
          field: 'installedCapacityDcMw',
          message: `Calculated DC capacity from panels (${fmt(calculatedDcMw, 4)} MW) differs from entered DC capacity (${fmt(dcMw, 4)} MW) by > 3%`,
        });
      }
    }

    setHints(nextHints);
  }, [form.inverterTotalPowerKw, form.installedCapacityAcMw, form.panelCount, form.panelNominalPowerW, form.installedCapacityDcMw]);

  const handleChange = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (key === 'dcAcRatio') {
      setManualDcAc(value.trim() !== '');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setApiError(null);
    try {
      const payload: Record<string, unknown> = {
        scadaAvailable: form.scadaAvailable === 'true',
        inverterType: form.inverterType || null,
        inverterCount: toNumberOrNull(form.inverterCount),
        panelType: form.panelType || null,
        panelCount: toNumberOrNull(form.panelCount),
        batteryCapacityMwh: toNumberOrNull(form.batteryCapacityMwh),
        gridConnectionVoltage: form.gridConnectionVoltage || null,
        meteringPointCode: form.meteringPointCode || null,
        scadaVendor: form.scadaVendor || null,
        communicationProtocol: form.communicationProtocol || null,
        dataRefreshInterval: form.dataRefreshInterval || null,
        tiltAngleDeg: toNumberOrNull(form.tiltAngleDeg),
        azimuthDeg: toNumberOrNull(form.azimuthDeg),
        installedCapacityDcMw: toNumberOrNull(form.installedCapacityDcMw),
        installedCapacityAcMw: toNumberOrNull(form.installedCapacityAcMw),
        panelNominalPowerW: toNumberOrNull(form.panelNominalPowerW),
        inverterTotalPowerKw: toNumberOrNull(form.inverterTotalPowerKw),
        dcAcRatio: toNumberOrNull(form.dcAcRatio),
      };

      console.log('TECH_SPEC_PATCH_PAYLOAD', payload);

      await api.patch(`/assets/${assetId}/technical-spec`, payload);
      addToast(t('at_saved_ok'), 'success');
      onSaved();
    } catch (err) {
      const message = getApiErrorMessage(err, t('at_save_failed'));
      setApiError(message);
      addToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const textMain = 'var(--text)';
  const textMuted = 'var(--text-muted)';
  const inputBg = 'var(--surface-solid)';
  const inputBorder = 'var(--line-strong)';
  const inputClass = 'w-full px-3 py-2 rounded-md text-sm outline-none transition-colors';
  const inputStyle = { background: inputBg, border: `1px solid ${inputBorder}`, color: textMain };
  const sectionBg = 'var(--surface-2)';
  const sectionBorder = 'var(--line)';

  const existingFields = [
    { key: 'inverterType', label: t('at_inverter_type'), placeholder: `${t('common_eg')} SMA Sunny Tripower` },
    { key: 'inverterCount', label: t('at_inverter_count'), placeholder: `${t('common_eg')} 12`, type: 'number' },
    { key: 'panelType', label: t('at_panel_type'), placeholder: `${t('common_eg')} JA Solar JAM72S30` },
    { key: 'panelCount', label: t('at_panel_count'), placeholder: `${t('common_eg')} 4800`, type: 'number' },
    { key: 'batteryCapacityMwh', label: t('at_battery_cap'), unit: 'MWh', placeholder: `${t('common_eg')} 2.5`, type: 'number' },
    { key: 'gridConnectionVoltage', label: t('at_grid_voltage'), placeholder: `${t('common_eg')} 110 kV` },
    { key: 'meteringPointCode', label: t('at_metering_code'), placeholder: `${t('common_eg')} UA-MP-00123456` },
    { key: 'scadaVendor', label: t('at_scada_vendor'), placeholder: `${t('common_eg')} Siemens WinCC` },
    { key: 'communicationProtocol', label: t('at_comm_protocol'), placeholder: `${t('common_eg')} Modbus TCP, IEC 61850` },
    { key: 'dataRefreshInterval', label: t('at_refresh'), placeholder: `${t('common_eg')} 5min, 15min` },
  ];

  const forecastingFields = [
    { key: 'tiltAngleDeg', label: t('at_tilt'), unit: '\u00B0', placeholder: '25', type: 'number', min: 0, max: 90, step: 0.1 },
    { key: 'azimuthDeg', label: t('at_azimuth'), unit: '\u00B0', placeholder: '180', type: 'number', min: 0, max: 360, step: 0.1 },
    { key: 'installedCapacityDcMw', label: t('at_installed_dc'), unit: 'MW', placeholder: '10.500', type: 'number', min: 0, step: 0.001 },
    { key: 'installedCapacityAcMw', label: t('at_installed_ac'), unit: 'MW', placeholder: '9.500', type: 'number', min: 0, step: 0.001 },
    { key: 'panelNominalPowerW', label: t('at_panel_power'), unit: 'W', placeholder: '550', type: 'number', min: 0, step: 0.1 },
    { key: 'inverterTotalPowerKw', label: t('at_inverter_power'), unit: 'kW', placeholder: '9500', type: 'number', min: 0, step: 0.1 },
    { key: 'dcAcRatio', label: t('at_dc_ac_ratio'), unit: t('at_unit_ratio'), placeholder: '1.1053', type: 'number', min: 0, step: 0.0001 },
  ];

  const getHintFor = (key: string): string | null => {
    const h = hints.find((hint) => hint.field === key);
    return h ? h.message : null;
  };

  const renderField = (
    field: {
      key: string;
      label: string;
      unit?: string;
      placeholder: string;
      type?: string;
      min?: number;
      max?: number;
      step?: number;
    },
    required = false
  ) => (
    <div key={field.key}>
      <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>
        {field.label}
        {field.unit && <span className="text-xs font-normal ml-1.5" style={{ color: textMuted }}>({field.unit})</span>}
        {required && <span className="text-xs ml-1" style={{ color: 'var(--accent)' }}>*</span>}
      </label>
      <input
        type={field.type ?? 'text'}
        min={field.min}
        max={field.max}
        step={field.step}
        value={form[field.key]}
        onChange={(e) => handleChange(field.key, e.target.value)}
        placeholder={field.placeholder}
        className={inputClass}
        style={inputStyle}
      />
      {getHintFor(field.key) && (
        <div className="mt-1.5 flex items-start gap-1.5 text-xs" style={{ color: 'var(--warn)' }}>
          <i className="ri-alert-line mt-0.5 flex-shrink-0"></i>
          <span>{getHintFor(field.key)}</span>
        </div>
      )}
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: 'var(--surface-2)' }}
          >
            <Settings size={15} strokeWidth={2} style={{ color: 'var(--text-muted)' }} />
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: textMain }}>{t('at_title')}</h3>
            <p className="text-xs" style={{ color: textMuted }}>
              {t('at_sub')}
            </p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors disabled:opacity-50 whitespace-nowrap"
          style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}
        >
          {saving ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i> {t('common_saving')}
            </>
          ) : (
            <>
              <i className="ri-save-line"></i> {t('common_save')}
            </>
          )}
        </button>
      </div>

      {/* API Error */}
      {apiError && (
        <div
          className="mb-4 px-4 py-3 rounded-lg text-sm"
          style={{ background: 'var(--surface-2)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}
        >
          <i className="ri-error-warning-line mr-2"></i>
          {apiError}
        </div>
      )}

      {/* Existing Technical Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {existingFields.map((f) => renderField(f))}

        <div className="md:col-span-2">
          <label
            className="flex items-center gap-3 cursor-pointer p-3 rounded-lg transition-colors"
            style={{ border: `1px solid ${inputBorder}`, background: 'var(--surface-solid)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-2)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--surface-solid)'; }}
          >
            <div className="relative">
              <input
                type="checkbox"
                checked={form.scadaAvailable === 'true'}
                onChange={(e) => handleChange('scadaAvailable', e.target.checked ? 'true' : 'false')}
                className="sr-only"
              />
              <div
                className="w-10 h-6 rounded-full transition-colors"
                style={{ background: form.scadaAvailable === 'true' ? 'var(--accent)' : isDark ? 'rgba(255,255,255,0.15)' : 'var(--line)' }}
              >
                <div
                  className="w-4 h-4 bg-white rounded-full absolute top-1 transition-transform"
                  style={{ left: form.scadaAvailable === 'true' ? '22px' : '4px' }}
                ></div>
              </div>
            </div>
            <div>
              <div className="text-sm font-medium" style={{ color: textMain }}>
                {t('at_scada_available')}
              </div>
              <div className="text-xs" style={{ color: textMuted }}>
                {t('at_scada_hint')}
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Технічні параметри прогнозування */}
      <div className="mb-5 flex items-center gap-2">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: 'var(--surface-2)' }}
        >
          <Sun size={15} strokeWidth={2} style={{ color: 'var(--text-muted)' }} />
        </div>
        <div>
          <h3 className="text-sm font-semibold" style={{ color: textMain }}>
            {t('at_fc_params_title')}
          </h3>
          <p className="text-xs" style={{ color: textMuted }}>
            {t('at_fc_params_sub')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {forecastingFields.map((f) => renderField(f, true))}
      </div>

      {/* Phase F 2026-05-15: equipment groups CRUD — declarative, separate from forecast formula */}
      <EquipmentGroupsSection type="panel" assetId={assetId} />
      <EquipmentGroupsSection type="inverter" assetId={assetId} />
    </div>
  );
}